// frontend/src/components/pantallaInformativa/VideoPantalla.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cargarApiYoutube } from '../../utils/apiYoutube';
import { analizarUrlYoutube } from '../../utils/youtube';

// Estados del reproductor de YouTube (YT.PlayerState).
const ESTADO_SIN_EMPEZAR = -1;
const ESTADO_TERMINADO = 0;
const ESTADO_REPRODUCIENDO = 1;
const ESTADO_EN_PAUSA = 2;
const ESTADO_PREPARADO = 5;
const ESTADOS_FRENADO = [ESTADO_SIN_EMPEZAR, ESTADO_EN_PAUSA, ESTADO_PREPARADO];
const MS_REINTENTO_API = 60000;
const MS_VIGILANCIA = 3000;

/**
 * El video del TV, con el reproductor de YouTube (IFrame Player API) para poder pausarlo y saber
 * cuándo termina. Qué video va y si está en pausa lo decide la página (ver colaDeVideos.js); acá
 * solo se carga en el reproductor.
 *
 * El reproductor se crea una sola vez y se le cambia el video: las actualizaciones de la cola (cada
 * pocos segundos) no lo reinician. En pausa, sin video o si YouTube no carga, se tapa con el cartel
 * del estudio: así el TV nunca queda en negro ni muestra los "Más videos" que YouTube pone al pausar.
 *
 * Un link con video (aunque venga de una lista) pasa ese video solo; un link de lista sin video pasa
 * la lista entera y recién al terminarla avisa onTermino.
 */
function VideoPantalla({
    video,
    reproduccion = 0,
    pausado = false,
    conSonido = false,
    onTermino,
    onError,
    onTitulo,
}) {
    const contenedorRef = useRef(null);
    const reproductorRef = useRef(null);
    const [listo, setListo] = useState(false);
    const [falloApi, setFalloApi] = useState(false);
    const [intentoApi, setIntentoApi] = useState(0);

    const datos = useMemo(() => analizarUrlYoutube(video?.url), [video?.url]);
    const hayVideo = Boolean(datos);
    const claveVideo = datos ? `${datos.videoId || ''}|${datos.listaId || ''}|${datos.inicio || ''}` : '';

    // Los eventos del reproductor se registran una vez: leen siempre los últimos valores de acá.
    const ultimos = useRef({});
    useEffect(() => {
        ultimos.current = { datos, pausado, onTermino, onError, onTitulo };
    });

    // Crea el reproductor cuando hay algo para pasar y lo destruye al desmontar. YouTube reemplaza
    // el nodo por su iframe, así que va en un div creado a mano y no en uno de React.
    useEffect(() => {
        if (!hayVideo) return undefined;
        const contenedor = contenedorRef.current;
        let cancelado = false;
        let reproductor = null;
        let reintento = null;

        cargarApiYoutube()
            .then((YT) => {
                if (cancelado || !contenedor) return;
                setFalloApi(false);
                const destino = document.createElement('div');
                contenedor.appendChild(destino);
                reproductor = new YT.Player(destino, {
                    width: '100%',
                    height: '100%',
                    playerVars: {
                        autoplay: 1,
                        mute: 1,
                        controls: 0,
                        rel: 0,
                        playsinline: 1,
                        modestbranding: 1,
                        iv_load_policy: 3,
                        disablekb: 1,
                        fs: 0,
                    },
                    events: {
                        onReady: (evento) => {
                            if (cancelado) return;
                            reproductorRef.current = evento.target;
                            setListo(true);
                        },
                        onStateChange: (evento) => {
                            if (cancelado) return;
                            const actual = evento.target;
                            if (evento.data === ESTADO_TERMINADO) {
                                // Dentro de una lista YouTube pasa solo al siguiente: la cola avanza
                                // recién al terminar la lista.
                                const lista = actual.getPlaylist?.();
                                const indice = actual.getPlaylistIndex?.();
                                if (Array.isArray(lista) && indice >= 0 && indice < lista.length - 1) return;
                                ultimos.current.onTermino?.();
                            } else if (evento.data === ESTADO_REPRODUCIENDO) {
                                // Arrancó justo cuando llegaba la pausa: se frena.
                                if (ultimos.current.pausado) {
                                    actual.pauseVideo();
                                    return;
                                }
                                ultimos.current.onTitulo?.(actual.getVideoData?.()?.title || '');
                            }
                        },
                        // Video borrado, privado o que no se deja insertar: que la cola siga.
                        onError: () => {
                            if (!cancelado) ultimos.current.onError?.();
                        },
                    },
                });
            })
            .catch(() => {
                if (cancelado) return;
                setFalloApi(true);
                reintento = setTimeout(() => setIntentoApi((n) => n + 1), MS_REINTENTO_API);
            });

        return () => {
            cancelado = true;
            clearTimeout(reintento);
            reproductorRef.current = null;
            setListo(false);
            try {
                reproductor?.destroy();
            } catch {
                // Ya se había ido: no hay nada que limpiar.
            }
            if (contenedor) contenedor.innerHTML = '';
        };
    }, [hayVideo, intentoApi]);

    // Cambio de video (o el mismo de nuevo, en una cola de uno). En pausa se deja preparado sin arrancar.
    useEffect(() => {
        const reproductor = reproductorRef.current;
        const actual = ultimos.current.datos;
        if (!listo || !reproductor || !actual) return;
        const arrancar = !ultimos.current.pausado;
        if (actual.videoId) {
            const opciones = { videoId: actual.videoId, startSeconds: actual.inicio || 0 };
            if (arrancar) reproductor.loadVideoById(opciones);
            else reproductor.cueVideoById(opciones);
        } else {
            const opciones = { list: actual.listaId, listType: 'playlist', index: 0, startSeconds: actual.inicio || 0 };
            if (arrancar) reproductor.loadPlaylist(opciones);
            else reproductor.cuePlaylist(opciones);
        }
    }, [listo, claveVideo, reproduccion]);

    // Solo cuando cambia la pausa: al cargar un video, el load/cue de arriba ya lo deja como corresponde.
    const pausadoAplicado = useRef(pausado);
    useEffect(() => {
        const reproductor = reproductorRef.current;
        if (!listo || !reproductor) {
            pausadoAplicado.current = pausado;
            return;
        }
        if (pausadoAplicado.current === pausado) return;
        pausadoAplicado.current = pausado;
        if (pausado) reproductor.pauseVideo();
        else reproductor.playVideo();
    }, [listo, pausado]);

    // YouTube no siempre arranca solo al cargar un video (depende del navegador, y a veces frena por su
    // cuenta), pero un playVideo() después sí. Mientras no esté en pausa, si quedó sin arrancar,
    // preparado o frenado, se lo vuelve a poner en marcha: un TV de kiosco no tiene quién toque play.
    useEffect(() => {
        if (!listo || pausado) return undefined;
        const vigilancia = setInterval(() => {
            const reproductor = reproductorRef.current;
            if (ESTADOS_FRENADO.includes(reproductor?.getPlayerState?.())) reproductor.playVideo();
        }, MS_VIGILANCIA);
        return () => clearInterval(vigilancia);
    }, [listo, pausado]);

    // El navegador solo deja arrancar con sonido después de un toque en la página (ver la página).
    useEffect(() => {
        const reproductor = reproductorRef.current;
        if (!listo || !reproductor) return;
        if (conSonido) {
            reproductor.unMute();
            reproductor.setVolume?.(100);
        } else {
            reproductor.mute();
        }
    }, [listo, conSonido]);

    const conCartel = !hayVideo || pausado || falloApi;

    return (
        <div className="pi-video" role="region" aria-label="Video de la sala de espera">
            <div className="pi-video__reproductor" ref={contenedorRef} />
            {conCartel && (
                <div className="pi-video__cartel">
                    <i className="bi bi-bank2 pi-video__icono" aria-hidden="true" />
                    <span className="pi-video__marca">Estudio Jurídico Previsional</span>
                    <span className="pi-video__bienvenida">Gracias por su paciencia, en breve lo atendemos</span>
                </div>
            )}
        </div>
    );
}

export default VideoPantalla;
